/**
 * @file src/local/localDuplicates.js
 * @description The same audio held twice, from the model that came before this one.
 *
 * Until a file belonged to a SET of collections, dropping it into a second collection minted a
 * second entry AND a second copy of the bytes. The store's version-2 upgrade turns each row's one
 * scope into a one-element set; it cannot turn two rows into one, because deciding that two rows
 * are the same file — and then deleting one of them — is not a migration's business. A person's
 * audio is on the other side of that decision.
 *
 * So this module only ever LOOKS. It is pure: entries in, groups out, nothing written, nothing
 * deleted. `localLibrary.js` owns the acting half, and the surface that shows a group is what
 * turns it into an act.
 *
 * ## Identity, stricter here than at import
 *
 * `refMatchesEntry` — the import path's test — falls back to name and size when a last-modified
 * time is missing on either side. That leniency is right there: the worst it costs is a collection
 * added to an entry, which the browser shows and one click undoes (decision 0006).
 *
 * It is NOT right here, where the outcome is a deleted entry and deleted bytes. So this asks for
 * all three facts, and for the timestamp to be a real one on BOTH rows. Two files that share a
 * name and a size but remember no time are left alone: they are probably the same file, and
 * "probably" is not enough to delete audio nobody can get back but from their own disk.
 *
 * ## Which one survives, and why it barely matters
 *
 * The rule is short because almost everything a duplicate carries can be ADOPTED by the survivor
 * — they are the same file, so its analysis, its remembered location, and an orbiter or world it
 * was given are all equally true of the one that stays. Rail positions are held per collection,
 * by id, so they are kept by rewriting the id in place rather than by choosing a row.
 *
 * What cannot be adopted is this browser's copy of the bytes, which is named after the entry that
 * owns it. So the copy is what the rule ranks on first: keep the row that already holds the bytes,
 * and no copy has to be moved for the merge to be safe. Then the one that can be reached, then the
 * oldest — the row the person actually made first.
 *
 * The one genuine loss is two rows each carrying a DIFFERENT orbiter or world. One of those
 * choices goes, and no rule can decide that honestly, so it is reported as a conflict for the
 * surface to say out loud before anyone agrees to anything.
 */

/** Analysis is adoptable, so it is not part of the ranking — but a row that has it is worth naming. */
function hasAnalysis(entry) {
  return Boolean(entry?.peaks && entry?.loudness);
}

/**
 * The three facts that make two rows the same file, or null when this row cannot be judged.
 *
 * Null is not "no duplicates": it is "do not group this one at all". A row with no usable
 * last-modified time never joins a group, and so is never anything's duplicate.
 */
export function localDuplicateKey(entry) {
  if (!entry || typeof entry.name !== 'string' || !entry.name) return null;
  const size = Number(entry.size);
  if (!Number.isFinite(size) || size <= 0) return null;
  const modified = Number(entry.lastModified);
  if (!Number.isFinite(modified) || modified <= 0) return null;
  // Joined on a character no file name can contain, so "a 1" + "2" and "a" + "1 2" cannot make the
  // same key. This key is what decides that two rows are one file, and a collision would merge them.
  return [entry.name, size, modified].join('\u0000');
}

/**
 * Rank two rows for which should survive. Lower sorts first, and first is kept.
 *
 * Only facts that cannot be carried over are ranked. Everything else the merge adopts.
 */
function keeperOrder(a, b, isReachable) {
  // The copy is the one thing named after the entry rather than the file. Keeping the row that
  // holds it means the merge never has to move bytes to be safe.
  const copied = Number(Boolean(b.copied)) - Number(Boolean(a.copied));
  if (copied) return copied;
  // A row whose file can still be found is worth more than one pointing at a moved file — though
  // the merge adopts a location too, so this only decides between two that both have none.
  const reach = Number(isReachable(b)) - Number(isReachable(a));
  if (reach) return reach;
  const added = (Number(a.addedAt) || 0) - (Number(b.addedAt) || 0);
  if (added) return added;
  // Never the insertion order of a Map: two runs of this over the same library must name the same
  // survivor, or a person shown one answer would be asked to agree to another.
  return String(a.id).localeCompare(String(b.id));
}

/**
 * A choice one row holds and the survivor does not share. `orbiter` and `world` are the only two
 * a person makes per file, and they are the only two a merge can lose.
 */
function conflictsBetween(keep, duplicates) {
  const conflicts = [];
  const clash = (field) =>
    duplicates.some((entry) => entry[field] && keep[field] && entry[field] !== keep[field]);
  if (clash('orbiterId')) conflicts.push('orbiter');
  if (clash('entangledWorldId')) conflicts.push('world');
  return conflicts;
}

/**
 * @typedef {object} LocalDuplicateGroup
 * @property {string} key the identity the rows share — a React key, and what a merge is asked for
 * @property {string} name the file's name, the same on every row in the group
 * @property {number} size one file's size in bytes
 * @property {object} keep the row that survives a merge
 * @property {object[]} duplicates the rows that would be removed, never empty
 * @property {string[]} scopes every collection the merged entry would belong to
 * @property {number} reclaimBytes what the disk gets back — only the copies that really exist
 * @property {boolean} adoptsAnalysis whether the survivor takes its waveform from a duplicate
 * @property {Array<'orbiter'|'world'>} conflicts choices a merge would lose, for the surface to say
 */

/**
 * Group a library's entries into the sets that are one file held several times.
 *
 * @param {Iterable<object>} entries every row on this machine
 * @param {{ isReachable?: (entry: object) => boolean }} [options] whether a row's file can be
 *   found right now. Defaults to true for every row, which is what a caller with no presence
 *   picture should assume — it only ever decides between rows that are otherwise equal.
 * @returns {LocalDuplicateGroup[]} groups of two or more, biggest reclaim first.
 */
export function groupLocalDuplicates(entries, { isReachable = () => true } = {}) {
  const byKey = new Map();
  for (const entry of entries || []) {
    const key = localDuplicateKey(entry);
    if (!key) continue;
    const bucket = byKey.get(key);
    if (bucket) bucket.push(entry);
    else byKey.set(key, [entry]);
  }
  const groups = [];
  for (const [key, rows] of byKey) {
    if (rows.length < 2) continue;
    const ordered = [...rows].sort((a, b) => keeperOrder(a, b, isReachable));
    const [keep, ...duplicates] = ordered;
    const scopes = [...new Set(ordered.flatMap((entry) => (Array.isArray(entry.scopes) ? entry.scopes : [])))];
    groups.push({
      key,
      name: keep.name,
      size: Number(keep.size) || 0,
      keep,
      duplicates,
      scopes,
      // Only the copies this browser really took. A duplicate that was never copied is a row in a
      // database; removing it gives back nothing, and saying it did would be a number that does
      // not match the disk the header measures.
      reclaimBytes: duplicates.reduce((sum, entry) => sum + (entry.copied ? Number(entry.size) || 0 : 0), 0),
      adoptsAnalysis: !hasAnalysis(keep) && duplicates.some(hasAnalysis),
      conflicts: conflictsBetween(keep, duplicates),
    });
  }
  return groups.sort((a, b) => b.reclaimBytes - a.reclaimBytes || a.name.localeCompare(b.name));
}

/**
 * What the survivor takes from the rows that go: every field that is true of the FILE rather than
 * of the row, and that the survivor does not already have.
 *
 * Analysis is taken as a set — peaks, loudness, duration and tempo come from one reading of one
 * file, and mixing halves of two readings would produce a waveform that belongs to neither. Any
 * field a pass writes belongs in this set: one left out arrives as "never analysed" on a row the
 * survivor marks READY, and nothing re-runs on its own to fill it in.
 *
 * @param {object} keep the surviving row
 * @param {object[]} duplicates the rows being removed
 * @returns {object} a patch for `keep`, empty when there is nothing to take
 */
export function adoptionPatch(keep, duplicates) {
  const patch = {};
  if (!hasAnalysis(keep)) {
    const analysed = duplicates.find(hasAnalysis);
    if (analysed) {
      patch.peaks = analysed.peaks;
      patch.loudness = analysed.loudness;
      patch.durationSec = analysed.durationSec ?? keep.durationSec ?? null;
      patch.tempoBpm = analysed.tempoBpm ?? null;
      patch.analysis = analysed.analysis;
    }
  } else if (keep.tempoBpm === undefined) {
    // The survivor's own reading predates tempo detection, so it is complete by the old definition
    // and adopts nothing above — yet a duplicate about to be deleted may hold the only tempo this
    // file has. Taking just that field is not mixing two readings: a tempo describes the FILE, and
    // both rows are the same file. `undefined` is the test on purpose — a stored `null` is an
    // answer ("no steady pulse") and is not overwritten by another row's guess.
    const timed = duplicates.find((entry) => typeof entry?.tempoBpm === 'number');
    if (timed) patch.tempoBpm = timed.tempoBpm;
  }
  // A way back to the file, taken only when the survivor has none at all. A handle and a path are
  // two platforms' answers to the same question, so either one counts as having one.
  if (!keep.handle && !keep.path) {
    const located = duplicates.find((entry) => entry.handle || entry.path);
    if (located) {
      if (located.handle) patch.handle = located.handle;
      if (located.path) patch.path = located.path;
      if (!keep.folder && located.folder) patch.folder = located.folder;
    }
  }
  // A choice the survivor never made. Where BOTH made one they differ, that is the conflict the
  // group reports and the survivor's own choice stands.
  if (!keep.orbiterId) {
    const chosen = duplicates.find((entry) => entry.orbiterId);
    if (chosen) {
      patch.orbiterId = chosen.orbiterId;
      patch.orbiterVersion = chosen.orbiterVersion ?? null;
    }
  }
  if (!keep.entangledWorldId) {
    const chosen = duplicates.find((entry) => entry.entangledWorldId);
    if (chosen) {
      patch.entangledWorldId = chosen.entangledWorldId;
      patch.entangledWorldVersion = chosen.entangledWorldVersion ?? null;
    }
  }
  return patch;
}

/**
 * A collection's rail order with the duplicates' ids folded onto the survivor's.
 *
 * The position is the person's arrangement of that rail, and it belongs to the collection, not to
 * whichever row happened to survive. So the FIRST id of the group in this list keeps its place and
 * becomes the survivor's; the rest drop out. An order that never mentioned the group is returned
 * unchanged, and the caller can tell because it is the same array.
 *
 * @param {string[]} ids the stored order
 * @param {string} keepId
 * @param {Iterable<string>} duplicateIds
 * @returns {string[]} the rewritten order
 */
export function foldRailOrder(ids, keepId, duplicateIds) {
  const drop = new Set(duplicateIds);
  if (!Array.isArray(ids) || !ids.some((id) => drop.has(id))) return ids;
  const out = [];
  let placed = false;
  for (const id of ids) {
    if (id !== keepId && !drop.has(id)) {
      out.push(id);
      continue;
    }
    // The first of the group's ids to appear — whichever row it was — is where the survivor goes.
    if (placed) continue;
    placed = true;
    out.push(keepId);
  }
  return out;
}
